export const PAGE_SIZE = 15;

export const COPY = {
  search: '搜尋',
  reset: '重置',
  view: '查看',
  status: '狀態',
  createdAt: '建立時間',
  emptyList: '尚無資料',
  readOnlyHint: '（唯讀）',
};

/** GroupTripRecruitmentStatus — source: GroupTripRecruitmentStatus.java */
export const GROUP_TRIP_STATUS_LABELS = {
  DRAFT: '草稿',
  OPEN: '開放中',
  CLOSED: '已關閉',
  CANCELLING: '取消中',
  CANCELLED: '已取消',
};

/** GroupTrip pricingType — source: admin query VO */
export const PRICING_TYPE_LABELS = {
  FREE: '免費',
  PAID: '付費',
};

/** TravelTradeStatus — source: TravelTradeStatus.java */
export const TRADE_STATUS_LABELS = {
  AWAITING_PAYMENT: '待付款',
  PAID: '已付款',
  REFUNDED: '已退款',
  CANCELLED: '已取消',
};

/** travel_social_report.status — source: SocialSafetyMapper */
export const SAFETY_STATUS_LABELS = {
  OPEN: '待處理',
  UNDER_REVIEW: '審核中',
  RESOLVED: '已結案',
};

/** SocialSafetyService.REASONS */
export const SAFETY_REASON_LABELS = {
  SPAM: '垃圾訊息',
  HARASSMENT: '騷擾',
  HATE_OR_ABUSE: '仇恨或辱罵',
  SEXUAL_CONTENT: '色情內容',
  VIOLENCE: '暴力',
  SCAM_OR_FRAUD: '詐騙',
  IMPERSONATION: '冒充',
  PRIVACY: '隱私',
  MISINFORMATION: '不實資訊',
  OTHER: '其他',
};

/** SocialSafetyService.TARGETS (admin filter subset) */
export const SAFETY_TARGET_LABELS = {
  SOCIAL_POST: '動態貼文',
  COMMENT: '留言',
  MEMBER: '會員',
  JOURNEY_PUBLICATION: '公開旅程',
  GROUP_TRIP: '揪團',
  CUSTOM_LOCATION: '自訂地點',
};

/** GroupTrip Phase-2 admin review (AUTH54): NO_ACTION / LIMIT / REMOVE. */
export const GROUP_TRIP_ADMIN_DECISION_OPTIONS = Object.freeze([
  { value: 'NO_ACTION', label: '不處置' },
  { value: 'LIMIT_DISTRIBUTION', label: '限制曝光' },
  { value: 'REMOVE_CONTENT', label: '移除內容' },
]);

export const SAFETY_GROUP_TRIP_PREVIEW_COPY = Object.freeze({
  section: '揪團預覽',
  title: '招募標題',
  summary: '摘要預覽',
  host: '主辦會員 ID',
  status: '招募狀態',
  visibility: '可見性',
  missing: '無法載入揪團預覽（目標可能已刪除或不存在）',
  noActionHint: '揪團檢舉可裁決「不處置／限制曝光／移除內容」。限制曝光隱藏公開發現但保留直接詳情；移除內容隱藏發現與公開詳情，並拒絕新申請。',
});

/** SocialSafetyService.DECISIONS */
export const SAFETY_DECISION_LABELS = {
  NO_ACTION: '不處置',
  LIMIT_DISTRIBUTION: '限制曝光',
  REMOVE_CONTENT: '移除內容',
  LIMIT_SOCIAL_DISTRIBUTION: '限制社群曝光',
};

/** Effective moderation target state (Admin-only). */
export const SAFETY_EFFECTIVE_STATE_LABELS = {
  ACTIVE: '正常（ACTIVE）',
  LIMITED: '限制曝光（LIMITED）',
  REMOVED: '已移除曝光（REMOVED）',
};

export const DESTRUCTIVE_SAFETY_DECISIONS = new Set([
  'LIMIT_DISTRIBUTION',
  'REMOVE_CONTENT',
  'LIMIT_SOCIAL_DISTRIBUTION',
]);

export const TRADE_MUTATION_ACTION_LABELS = [];

export const EMPTY_GROUP_TRIP_FILTERS = {
  groupTripId: '',
  hostMemberId: '',
  recruitmentStatus: '',
  pricingType: '',
  currencyCode: '',
};

export const GROUP_TRIP_STATUS_OPTIONS = [
  { value: '', label: '全部' },
  ...Object.entries(GROUP_TRIP_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

export const PRICING_TYPE_OPTIONS = [
  { value: '', label: '全部' },
  ...Object.entries(PRICING_TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

export function buildGroupTripPageParams(pageNo, filters, pageSize = PAGE_SIZE) {
  return {
    pageNo,
    pageSize,
    groupTripId: filters.groupTripId || undefined,
    hostMemberId: filters.hostMemberId || undefined,
    recruitmentStatus: filters.recruitmentStatus || undefined,
    pricingType: filters.pricingType || undefined,
    currencyCode: filters.currencyCode || undefined,
  };
}

export const EMPTY_TRADE_FILTERS = {
  tradeId: '',
  groupTripId: '',
  buyerMemberId: '',
  hostMemberId: '',
  status: '',
  currencyCode: '',
};

export const TRADE_STATUS_OPTIONS = [
  { value: '', label: '全部' },
  ...Object.entries(TRADE_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

export function buildTradePageParams(pageNo, filters, pageSize = PAGE_SIZE) {
  return {
    pageNo,
    pageSize,
    tradeId: filters.tradeId || undefined,
    groupTripId: filters.groupTripId || undefined,
    buyerMemberId: filters.buyerMemberId || undefined,
    hostMemberId: filters.hostMemberId || undefined,
    status: filters.status || undefined,
    currencyCode: filters.currencyCode || undefined,
  };
}

export const EMPTY_SAFETY_FILTERS = {
  status: '',
  targetType: '',
  targetId: '',
};

export const SAFETY_STATUS_OPTIONS = [
  { value: '', label: '全部' },
  ...Object.entries(SAFETY_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

export const SAFETY_TARGET_OPTIONS = [
  { value: '', label: '全部' },
  ...Object.entries(SAFETY_TARGET_LABELS).map(([value, label]) => ({ value, label })),
];

export const SAFETY_DECISION_OPTIONS = Object.entries(SAFETY_DECISION_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/** Custom Location 治理：維持上架／下架（對齊 SocialSafetyService.CUSTOM_LOCATION_ALLOWED_ADMIN_DECISIONS）。 */
export const CUSTOM_LOCATION_ADMIN_DECISION_OPTIONS = Object.freeze([
  { value: 'NO_ACTION', label: '維持上架' },
  { value: 'REMOVE_CONTENT', label: '下架' },
]);

/**
 * Target-aware decision options. GROUP_TRIP Phase-2: NO_ACTION / LIMIT_DISTRIBUTION / REMOVE_CONTENT.
 * CUSTOM_LOCATION: NO_ACTION / REMOVE_CONTENT.
 * Other targets keep the full SocialSafetyService.DECISIONS set.
 */
export function safetyDecisionOptionsForTarget(targetType) {
  const code = normalizeCode(targetType);
  if (code === 'GROUP_TRIP') {
    return [...GROUP_TRIP_ADMIN_DECISION_OPTIONS];
  }
  if (code === 'CUSTOM_LOCATION') {
    return [...CUSTOM_LOCATION_ADMIN_DECISION_OPTIONS];
  }
  return SAFETY_DECISION_OPTIONS;
}

export function isGroupTripSafetyTarget(targetType) {
  return normalizeCode(targetType) === 'GROUP_TRIP';
}

export function hasGroupTripSafetyPreview(report) {
  if (!report || !isGroupTripSafetyTarget(report.targetType)) {
    return false;
  }
  return Boolean(
    report.recruitmentTitle
      || report.summaryPreview
      || report.hostMemberId
      || report.recruitmentStatus
      || report.visibility,
  );
}

export function buildSafetyPageParams(pageNo, filters, pageSize = PAGE_SIZE) {
  return {
    pageNo,
    pageSize,
    status: filters.status || undefined,
    targetType: filters.targetType || undefined,
    targetId: filters.targetId || undefined,
  };
}

export function formatDateCell(value) {
  if (!value) return '—';
  return String(value).replace('T', ' ').slice(0, 19);
}

export function formatAmount(value) {
  if (value == null || value === '') {
    return '—';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

function normalizeCode(value) {
  if (value == null || value === '') {
    return '';
  }
  return String(typeof value === 'string' ? value : value?.name || value?.code || value).toUpperCase();
}

export function formatGroupTripStatus(status) {
  const code = normalizeCode(status);
  if (!code) return '—';
  return GROUP_TRIP_STATUS_LABELS[code] || code;
}

export function groupTripStatusClassName(status) {
  const code = normalizeCode(status);
  if (code === 'OPEN') return 'text-pending';
  if (code === 'CLOSED') return 'text-profit';
  if (code === 'CANCELLING' || code === 'CANCELLED') return 'text-loss';
  if (code === 'DRAFT') return 'text-pending';
  return '';
}

export function formatPricingType(pricingType) {
  const code = normalizeCode(pricingType);
  if (!code) return '—';
  return PRICING_TYPE_LABELS[code] || code;
}

export function formatTradeStatus(status) {
  const code = normalizeCode(status);
  if (!code) return '—';
  return TRADE_STATUS_LABELS[code] || code;
}

export function tradeStatusClassName(status) {
  const code = normalizeCode(status);
  if (code === 'AWAITING_PAYMENT') return 'text-pending';
  if (code === 'PAID') return 'text-profit';
  if (code === 'REFUNDED' || code === 'CANCELLED') return 'text-loss';
  return '';
}

export function formatSafetyStatus(status) {
  const code = normalizeCode(status);
  if (!code) return '—';
  return SAFETY_STATUS_LABELS[code] || code;
}

export function safetyStatusClassName(status) {
  const code = normalizeCode(status);
  if (code === 'OPEN' || code === 'UNDER_REVIEW') return 'text-pending';
  if (code === 'RESOLVED') return 'text-profit';
  return '';
}

export function formatSafetyReason(reason) {
  const code = normalizeCode(reason);
  if (!code) return '—';
  return SAFETY_REASON_LABELS[code] || code;
}

export function formatSafetyTargetLabel(targetType) {
  const code = normalizeCode(targetType);
  return SAFETY_TARGET_LABELS[code] || targetType || '—';
}

export function formatSafetyTechnicalId(targetId) {
  if (targetId == null || targetId === '') return '';
  return String(targetId);
}

/** Existing API enrichment only — never invent a title or member name. */
export function safetyTargetContext(report) {
  const title = String(report?.recruitmentTitle || '').trim();
  return title;
}

export function formatSafetyTarget(targetType, targetId) {
  void targetId;
  return formatSafetyTargetLabel(targetType);
}

export const SAFETY_VISIBILITY_LABELS = {
  PUBLIC: '所有人',
  FOLLOWERS: '追蹤者',
  PRIVATE: '僅自己',
};

export function formatSafetyVisibility(visibility) {
  const code = normalizeCode(visibility);
  if (!code) return '—';
  return SAFETY_VISIBILITY_LABELS[code] || code;
}

export function formatSafetyDecision(decision) {
  const code = normalizeCode(decision);
  if (!code) return '—';
  return SAFETY_DECISION_LABELS[code] || code;
}

export function formatEffectiveModerationState(state) {
  const code = normalizeCode(state);
  if (!code) return SAFETY_EFFECTIVE_STATE_LABELS.ACTIVE;
  return SAFETY_EFFECTIVE_STATE_LABELS[code] || code;
}

export function effectiveModerationStateClassName(state) {
  const code = normalizeCode(state) || 'ACTIVE';
  if (code === 'LIMITED') return 'text-pending';
  if (code === 'REMOVED') return 'text-loss';
  return 'text-profit';
}

export function isDestructiveSafetyDecision(decision) {
  return DESTRUCTIVE_SAFETY_DECISIONS.has(normalizeCode(decision));
}

export function shouldConfirmSafetyDecision(decision) {
  return isDestructiveSafetyDecision(decision);
}

export function resolveSafetyCaseId(payload) {
  if (!payload || typeof payload !== 'object') {
    return '';
  }
  const raw = payload.caseId ?? payload.id ?? payload.case_id;
  if (raw == null || raw === '') {
    return '';
  }
  return String(raw);
}

export function safetyCaseId(caseDraft) {
  return resolveSafetyCaseId(caseDraft);
}

export function isSafetyCaseOpen(caseDraft) {
  const status = normalizeCode(caseDraft?.status);
  // Legacy payloads without status are treated as open so decide can still run.
  return !status || status === 'OPEN';
}

export function canSubmitSafetyDecision({ deciding, caseDraft }) {
  if (deciding || !safetyCaseId(caseDraft)) {
    return false;
  }
  if (!caseDraft.decision) {
    return false;
  }
  return isSafetyCaseOpen(caseDraft);
}

export function maskIdentifier(value, { prefixLen = 6, suffixLen = 4 } = {}) {
  if (value == null || value === '') {
    return '—';
  }
  const str = String(value);
  if (str.length <= prefixLen + suffixLen + 1) {
    return str;
  }
  return `${str.slice(0, prefixLen)}…${str.slice(-suffixLen)}`;
}

export const PROMOTION_STATUS_LABELS = {
  DRAFT: '草稿',
  SUBMITTED: '待審核',
  UNDER_REVIEW: '審核中',
  APPROVED: '已通過',
  REJECTED: '已駁回',
  CANCELLED: '已取消',
};

export const CREATIVE_STATUS_LABELS = {
  DRAFT: '草稿',
  SUBMITTED: '待審核',
  UNDER_REVIEW: '審核中',
  APPROVED: '已通過',
  REJECTED: '已駁回',
};

export const CAMPAIGN_STATUS_LABELS = {
  DRAFT: '草稿',
  READY: '就緒',
  ACTIVE: '投放中',
  PAUSED: '已暫停',
  SUSPENDED: '強制暫停',
  CANCELLED: '已終止',
};

export function formatPromotionStatus(status) {
  const code = normalizeCode(status);
  return PROMOTION_STATUS_LABELS[code] || (status ? String(status) : '—');
}

export function formatCreativeStatus(status) {
  const code = normalizeCode(status);
  return CREATIVE_STATUS_LABELS[code] || (status ? String(status) : '—');
}

export function formatCampaignStatus(status) {
  const code = normalizeCode(status);
  return CAMPAIGN_STATUS_LABELS[code] || (status ? String(status) : '—');
}

/** MemberCustomLocationStatus — source: MemberCustomLocationStatus.java */
export const CUSTOM_LOCATION_STATUS_LABELS = {
  PRIVATE: '僅自己',
  PENDING_PUBLIC: '舊待審（遺留）',
  PUBLIC: '已公開',
  REJECTED: '已拒絕',
  REMOVED: '已由平台下架',
};

export function formatCustomLocationStatus(status) {
  const code = normalizeCode(status);
  if (!code) return '—';
  return CUSTOM_LOCATION_STATUS_LABELS[code] || code;
}

export function customLocationStatusClassName(status) {
  const code = normalizeCode(status);
  if (code === 'PUBLIC') return 'badge-success';
  if (code === 'PENDING_PUBLIC') return 'badge-warning';
  if (code === 'REJECTED') return 'badge-danger';
  if (code === 'REMOVED') return 'badge-secondary';
  return 'badge-light';
}

export const TRAVEL_MANAGE_ROUTES = [
  '/system/travel-manage/group-trip-list',
  '/system/travel-manage/trade-list',
  '/system/travel-manage/safety-reports',
  '/system/travel-manage/custom-location-review',
];
